import { DropzoneImage, DropzoneValue } from '../types';

/** Одна миниатюра: всё, что нужно нарисовать, и ничего лишнего. */
export interface DropzonePreview {
  key: string;
  url?: string;
  name?: string;
  /** Вес известен только у файла, который выбрали сейчас; у сохранённой картинки — если его прислал бэкенд. */
  size?: number;
}

/** Значение поля приводится к списку: одиночное поле — просто список из одного. */
export function toItems(value: unknown): DropzoneValue[] {
  if (Array.isArray(value)) return value.filter(Boolean) as DropzoneValue[];
  return value ? [value as DropzoneValue] : [];
}

/**
 * Список значений — в то, что рисует превью.
 *
 * Адрес для файла берётся снаружи (`resolveUrl`), а не создаётся здесь: объектные
 * адреса нужно отзывать, иначе файл остаётся в памяти вкладки, и заведовать этим
 * должен тот, кто знает про жизненный цикл компонента.
 */
export function toPreviews(items: DropzoneValue[], resolveUrl: (file: File) => string): DropzonePreview[] {
  return items.map((item, index) => {
    if (item instanceof File) {
      return { key: `file:${item.name}:${index}`, url: resolveUrl(item), name: item.name, size: item.size };
    }

    if (typeof item === 'string') {
      return { key: `url:${item}`, url: item, name: item };
    }

    const url = item.url || item.externalUrl;
    return { key: `image:${item.id ?? url ?? index}`, url, name: item.name, size: item.size };
  });
}

/** Файлы из значения поля — те картинки, для которых нужен объектный адрес. */
export function filesOf(items: DropzoneValue[]): File[] {
  return items.filter((item): item is File => item instanceof File);
}

/**
 * Переставить картинку.
 *
 * Порядок — это не только вид: у локаций он уезжает на бэкенд номером
 * `ordinalNumber`, и карточка показывает фотографии в нём.
 */
export function moveItem(items: DropzoneValue[], from: number, to: number): DropzoneValue[] {
  const result = [...items];
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);

  return renumber(result);
}

/**
 * Заменить картинку на месте: улучшенный файл встаёт туда, где был исходник.
 *
 * Номера не пересчитываются — место то же, а позицию файла бэкенду передаёт форма (у
 * локаций — индексом загрузки).
 */
export function replaceItem(items: DropzoneValue[], index: number, next: DropzoneValue): DropzoneValue[] {
  return items.map((item, position) => (position === index ? next : item));
}

export function removeItem(items: DropzoneValue[], index: number): DropzoneValue[] {
  return renumber(items.filter((_, position) => position !== index));
}

/**
 * Проставить порядковые номера сохранённым картинкам.
 *
 * Файлы остаются собой: прежняя дропзона пересобирала каждый через
 * `new File([file], …)` — то есть копировала содержимое в памяти на каждое
 * удаление и каждое перетаскивание, при пяти фотографиях по четыре мегабайта
 * это заметно, а смысла в этом не было никакого.
 */
function renumber(items: DropzoneValue[]): DropzoneValue[] {
  return items.map((item, index) => {
    if (item instanceof File || typeof item === 'string') return item;
    return { ...(item as DropzoneImage), ordinalNumber: index };
  });
}
